/**
 * Reads JSON that is still streaming in and returns everything that has arrived so far.
 *
 * Unfinished strings are returned with the text received so far (so code can be shown while it is being
 * written), unfinished objects and arrays are returned with their complete members, and a member whose value
 * has not started yet is left out. Returns undefined when nothing usable has arrived.
 */
export function parsePartialJson(text: string): unknown {
  const reader = new PartialJsonReader(text);
  try {
    return reader.value();
  } catch {
    return undefined;
  }
}

/** Marks a value that was cut off before it was complete (strings are the exception: they are kept). */
const INCOMPLETE = Symbol('incomplete');

class PartialJsonReader {
  private i = 0;

  constructor(private readonly s: string) {}

  value(): unknown {
    this.space();
    if (this.i >= this.s.length) return undefined;
    const ch = this.s[this.i];
    if (ch === '{') return this.object();
    if (ch === '[') return this.array();
    if (ch === '"') return this.string().value;
    const literal = this.literal();
    return literal === INCOMPLETE ? undefined : literal;
  }

  private object(): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    this.i++;
    while (true) {
      this.space();
      if (this.i >= this.s.length) return result;
      if (this.s[this.i] === '}') { this.i++; return result; }
      if (this.s[this.i] === ',') { this.i++; continue; }
      if (this.s[this.i] !== '"') throw new Error('Expected a key');
      const key = this.string();
      if (!key.complete) return result;
      this.space();
      if (this.i >= this.s.length) return result;
      if (this.s[this.i] !== ':') throw new Error('Expected a colon');
      this.i++;
      this.space();
      if (this.i >= this.s.length) return result;
      const value = this.member();
      if (value !== INCOMPLETE) result[key.value] = value;
      if (this.i >= this.s.length) return result;
    }
  }

  private array(): unknown[] {
    const result: unknown[] = [];
    this.i++;
    while (true) {
      this.space();
      if (this.i >= this.s.length) return result;
      if (this.s[this.i] === ']') { this.i++; return result; }
      if (this.s[this.i] === ',') { this.i++; continue; }
      const value = this.member();
      if (value !== INCOMPLETE) result.push(value);
      if (this.i >= this.s.length) return result;
    }
  }

  /** A value inside an object or array; a cut-off literal or number is reported as INCOMPLETE. */
  private member(): unknown {
    const ch = this.s[this.i];
    if (ch === '{') return this.object();
    if (ch === '[') return this.array();
    if (ch === '"') return this.string().value;
    const start = this.i;
    const value = this.literal();
    return this.i >= this.s.length && !/^(true|false|null)$/.test(this.s.slice(start).trim()) ? INCOMPLETE : value;
  }

  private string(): { value: string; complete: boolean } {
    this.i++;
    let out = '';
    while (this.i < this.s.length) {
      const ch = this.s[this.i];
      if (ch === '"') { this.i++; return { value: out, complete: true }; }
      if (ch !== '\\') { out += ch; this.i++; continue; }
      const next = this.s[this.i + 1];
      if (next === undefined) { this.i = this.s.length; break; }
      if (next === 'u') {
        const hex = this.s.slice(this.i + 2, this.i + 6);
        if (hex.length < 4) { this.i = this.s.length; break; }
        out += String.fromCharCode(parseInt(hex, 16));
        this.i += 6;
        continue;
      }
      const escapes: Record<string, string> = { n: '\n', t: '\t', r: '\r', b: '\b', f: '\f', '"': '"', '\\': '\\', '/': '/' };
      out += escapes[next] ?? next;
      this.i += 2;
    }
    return { value: out, complete: false };
  }

  private literal(): unknown {
    const match = /^(true|false|null|-?\d+(\.\d+)?([eE][+-]?\d+)?)/.exec(this.s.slice(this.i));
    if (!match) {
      this.i = this.s.length;
      return INCOMPLETE;
    }
    this.i += match[0].length;
    return JSON.parse(match[0]);
  }

  private space(): void {
    while (this.i < this.s.length && /\s/.test(this.s[this.i])) this.i++;
  }
}
